import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePublicHomepageContent } from "@/hooks/api/use-public";
import { Skeleton } from "@/components/ui/skeleton";
import { DynamicIcon } from "@/components/ui/DynamicIcon";
import OptimizedImage from "@/components/ui/OptimizedImage";
import { getFullImageUrl } from "@/lib/image-utils";
import { useState, useEffect } from "react";

const HERO_BG_STORAGE_KEY = 'hero_background_image';

const HeroSection = () => {
  const { t, isRTL, language } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;
  const { data: homepageContent, isLoading } = usePublicHomepageContent();
  
  // State for background image with localStorage caching
  const [cachedBackgroundImage, setCachedBackgroundImage] = useState<string | null>(() => {
    // Initialize from localStorage on first render
    try {
      return localStorage.getItem(HERO_BG_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Debug: Log the API response
  if (import.meta.env.DEV) {
    console.log('🏠 Homepage API Response:', homepageContent);
  }

  // API returns { success: true, data: { content: {...}, features: [], statistics: [] } }
  const content = homepageContent?.data?.content;

  // Get hero content from API - no static fallback, use shimmer loading instead
  const heroTitle = content?.hero_title 
    ? (typeof content.hero_title === 'string' 
        ? decodeURIComponent(content.hero_title)
        : decodeURIComponent(content.hero_title[language] || content.hero_title.en))
    : null;

  const heroDescription = content?.hero_description
    ? (typeof content.hero_description === 'string'
        ? decodeURIComponent(content.hero_description)
        : decodeURIComponent(content.hero_description[language] || content.hero_description.en))
    : null;

  const heroCtaText = content?.hero_cta_text
    ? (typeof content.hero_cta_text === 'string'
        ? decodeURIComponent(content.hero_cta_text)
        : decodeURIComponent(content.hero_cta_text[language] || content.hero_cta_text.en))
    : null;

  const heroCtaUrl = content?.hero_cta_url || '/auth?mode=register';
  const heroTitleColor = content?.hero_title_color || '#ffffff';
  
  // Update cached background image when API returns new image
  useEffect(() => {
    if (content?.hero_background_image) {
      const apiImage = getFullImageUrl(content.hero_background_image) || '';
      
      // Check if image is new or updated (compare with cached version)
      if (cachedBackgroundImage !== apiImage) {
        try {
          localStorage.setItem(HERO_BG_STORAGE_KEY, apiImage);
          setCachedBackgroundImage(apiImage);
          console.log('🖼️ Hero background image cached/updated');
        } catch (error) {
          console.warn('Failed to cache hero background image:', error);
        }
      }
    }
  }, [content?.hero_background_image, cachedBackgroundImage]);
  
  // Use cached image, API image, or show shimmer
  const backgroundImage = cachedBackgroundImage || getFullImageUrl(content?.hero_background_image);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        {backgroundImage ? (
          <OptimizedImage
            src={backgroundImage}
            alt="Construction site"
            priority={true}
            className="w-full h-full object-cover"
            containerClassName="absolute inset-0"
          />
        ) : (
          <div className="w-full h-full animate-pulse bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700 bg-[length:200%_100%]" 
               style={{ animation: 'shimmer 1.5s infinite' }} />
        )}
        <div className="absolute inset-0 z-10 bg-black/40" />
      </div>

      {/* Content */}
      <div className="relative z-20 container mx-auto px-4 text-center">
        <div className="max-w-4xl mx-auto">
          {isLoading || !heroTitle ? (
            <>
              <Skeleton className="h-20 w-full max-w-3xl mx-auto mb-6" />
              <Skeleton className="h-6 w-full max-w-2xl mx-auto mb-10" />
            </>
          ) : (
            <>
              <h1 
                className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-up"
                style={{ color: heroTitleColor }}
              >
                {heroTitle}
              </h1>
              <p className="text-lg md:text-xl text-background/80 mb-10 max-w-2xl mx-auto animate-fade-up stagger-1">
                {heroDescription}
              </p>
            </>
          )}
          {!isAuthenticated && !isLoading && heroCtaText && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up stagger-2">
              <Button size="lg" className="hero" asChild>
                <Link to={heroCtaUrl}>
                  {heroCtaText}
                  <ArrowIcon size={20} />
                </Link>
              </Button>
              <Button variant="heroOutline" size="lg" className="bg-background/10 text-background border-background/30 hover:bg-background/20" asChild>
                <Link to="/how-it-works">
                  {t('hero.learnMore')}
                </Link>
              </Button>
            </div>
          )}
          {isAuthenticated && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up stagger-2">
              <Button size="lg" className="hero" asChild>
                <Link to={user?.type === 'admin' ? '/admin/dashboard' : '/dashboard'}>
                  {t('cta.dashboard.button') || 'لوحة التحكم'}
                  <ArrowIcon size={20} />
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 border-2 border-background/50 rounded-full flex items-start justify-center pt-2">
          <div className="w-1 h-2 bg-background/50 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = () => {
  const { t, language } = useLanguage();
  const { data: homepageContent, isLoading } = usePublicHomepageContent();

  // API returns { success: true, data: { content: {...}, features: [], statistics: [] } }
  const content = homepageContent?.data?.content;

  const sectionSubtitle = content?.section_subtitle
    ? (typeof content.section_subtitle === 'string'
        ? decodeURIComponent(content.section_subtitle)
        : decodeURIComponent(content.section_subtitle[language] || content.section_subtitle.en))
    : null;

  const features = homepageContent?.data?.features?.filter(f => f.is_active) || [];

  return (
    <section className="py-24 bg-muted">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 animate-fade-up">
            {t('features.title')}
          </h2>
          {isLoading ? (
            <Skeleton className="h-6 w-full max-w-xl mx-auto" />
          ) : (
            <p className="text-muted-foreground max-w-xl mx-auto animate-fade-up stagger-1">
              {sectionSubtitle}
            </p>
          )}
        </div>

        {isLoading || features.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-64 rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const title = typeof feature.title === 'string' 
                ? feature.title 
                : (feature.title?.[language] || feature.title?.en || '');
              const description = typeof feature.description === 'string'
                ? feature.description
                : (feature.description?.[language] || feature.description?.en || '');

              return (
                <div
                  key={feature.id}
                  className={`bg-card p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 animate-fade-up stagger-${index + 1}`}
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6">
                    {feature.icon ? (
                      <DynamicIcon 
                        iconName={feature.icon} 
                        className="w-8 h-8 text-primary" 
                        size={32}
                        fallback={<span className="text-2xl">📄</span>}
                      />
                    ) : (
                      <span className="text-2xl">📄</span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {description}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

const StatsSection = () => {
  const { t, language } = useLanguage();
  const { data: homepageContent, isLoading } = usePublicHomepageContent();

  const statistics = homepageContent?.data?.statistics?.filter(s => s.is_active) || [];

  return (
    <section className="py-20 bg-secondary">
      <div className="container mx-auto px-4">
        {isLoading || statistics.length === 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {statistics.map((stat, index) => {
              const label = typeof stat.label === 'string'
                ? stat.label
                : (stat.label?.[language] || stat.label?.en || '');
              const number = 'number' in stat ? stat.number : '0';

              return (
                <div key={stat.id} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                    {number}
                  </div>
                  <div className="text-secondary-foreground/80">
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

const CTASection = () => {
  const { t, isRTL } = useLanguage();
  const { isAuthenticated, user } = useAuth();
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  // Show different content for authenticated vs non-authenticated users
  if (isAuthenticated) {
    const dashboardUrl = user?.type === 'admin' ? '/admin/dashboard' : '/dashboard';
    
    return (
      <section className="py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              {user?.type === 'admin' 
                ? t('cta.dashboard.title.admin')
                : t('cta.dashboard.title.client')
              }
            </h2>
            <p className="text-lg text-muted-foreground mb-10">
              {user?.type === 'admin'
                ? t('cta.dashboard.subtitle.admin')
                : t('cta.dashboard.subtitle.client')
              }
            </p>
            <Button size="lg" asChild>
              <Link to={dashboardUrl}>
                {t('cta.dashboard.button')}
                <ArrowIcon size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 bg-muted">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
            {t('cta.title')}
          </h2>
          <p className="text-lg text-muted-foreground mb-10">
            {t('cta.subtitle')}
          </p>
          <Button size="lg" asChild>
            <Link to="/auth?mode=register">
              {t('cta.button')}
              <ArrowIcon size={20} />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

const Index = () => {
  return (
    <>
      <HeroSection />
      <FeaturesSection />
      <StatsSection />
      <CTASection />
    </>
  );
};

export default Index;
